import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DealInfo, ContactInfo, useCompleteSession } from '../../hooks/useDocumentCollection';
import { getDocumentSections, DocumentTypeConfig } from '../../config/documentTypes';
import DocumentDropzone from './DocumentDropzone';
import { ContactInfoForm } from './ContactInfoForm';

interface DocumentUploadFormProps {
  dealId: string;
  dealInfo: DealInfo;
  formType: 'standard' | 'retrospective';
  onSuccess: (documentsCount: number, contactEmail: string) => void;
}

interface UploadState {
  file: File | null;
  status: 'idle' | 'uploading' | 'success' | 'error';
  uploadId: string | null;
  error: string | null;
}

const initialContactInfo: ContactInfo = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
};

const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({
  dealId,
  dealInfo,
  formType,
  onSuccess,
}) => {
  // Session tracking - use both state (for rendering) and ref (for immediate access)
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  // Track uploads by document type key
  const [uploads, setUploads] = useState<Map<string, UploadState>>(new Map());

  // Contact form state
  const [contactInfo, setContactInfo] = useState<ContactInfo>(initialContactInfo);
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});

  // Form submission
  const { completeSession, submitting, error: submitError } = useCompleteSession();

  // Session error state
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Get document sections based on form type
  const sections = useMemo(() => getDocumentSections(formType), [formType]);

  // Count successful uploads
  const uploadedCount = useMemo(() => {
    let count = 0;
    uploads.forEach((upload) => {
      if (upload.status === 'success') {
        count++;
      }
    });
    return count;
  }, [uploads]);

  // Check if any upload is in progress
  const isUploading = useMemo(() => {
    let uploading = false;
    uploads.forEach((upload) => {
      if (upload.status === 'uploading') {
        uploading = true;
      }
    });
    return uploading;
  }, [uploads]);

  // Validate contact form
  const validateContactForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!contactInfo.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!contactInfo.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!contactInfo.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setContactErrors(errors);
    return Object.keys(errors).length === 0;
  }, [contactInfo]);

  // Check if form is valid for submission
  const canSubmit = useMemo(() => {
    const hasUploads = uploadedCount > 0;
    const hasRequiredContact =
      contactInfo.firstName.trim() !== '' &&
      contactInfo.lastName.trim() !== '' &&
      contactInfo.email.trim() !== '' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email);

    return hasUploads && hasRequiredContact && !isUploading && !submitting;
  }, [uploadedCount, contactInfo, isUploading, submitting]);

  // Warn user before leaving page with pending uploads or unsubmitted files
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isUploading || (uploadedCount > 0 && !submitting)) {
        e.preventDefault();
        // Modern browsers ignore custom messages but still show a generic warning
        e.returnValue = 'You have uploads in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading, uploadedCount, submitting]);

  // Handle upload start
  const handleUploadStart = useCallback((documentType: string) => {
    setUploads((prev) => {
      const next = new Map(prev);
      next.set(documentType, {
        file: null,
        status: 'uploading',
        uploadId: null,
        error: null,
      });
      return next;
    });
  }, []);

  // Handle upload complete
  const handleUploadComplete = useCallback(
    (documentType: string, uploadId: string, newSessionId: string) => {
      // Update session ID if this is the first upload
      // Use ref for immediate check to prevent race condition with rapid uploads
      if (!sessionIdRef.current) {
        sessionIdRef.current = newSessionId;
        setSessionId(newSessionId);
      }

      setUploads((prev) => {
        const next = new Map(prev);
        next.set(documentType, {
          file: null,
          status: 'success',
          uploadId,
          error: null,
        });
        return next;
      });
    },
    []
  );

  // Handle upload error
  const handleUploadError = useCallback((documentType: string, error: string) => {
    setUploads((prev) => {
      const next = new Map(prev);
      next.set(documentType, {
        file: null,
        status: 'error',
        uploadId: null,
        error,
      });
      return next;
    });
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    // Clear previous session error
    setSessionError(null);

    if (!validateContactForm()) {
      return;
    }

    if (!sessionId) {
      setSessionError('Unable to submit: No upload session found. Please upload at least one document before submitting.');
      return;
    }

    const result = await completeSession(sessionId, contactInfo);

    if (result) {
      onSuccess(result.documentsUploaded, contactInfo.email);
    }
  }, [sessionId, contactInfo, validateContactForm, completeSession, onSuccess]);

  // Render a document dropzone
  // Note: sessionIdRef.current is used to prevent race conditions with rapid uploads
  const renderDropzone = useCallback(
    (docType: DocumentTypeConfig) => {
      const uploadState = uploads.get(docType.key);
      const isDisabled = uploadState?.status === 'uploading';

      return (
        <Box key={docType.key} sx={{ mb: 2 }}>
          <DocumentDropzone
            documentType={docType}
            dealId={dealId}
            sessionId={sessionIdRef.current}
            formType={formType}
            onUploadStart={() => handleUploadStart(docType.key)}
            onUploadComplete={(uploadId, newSessionId) =>
              handleUploadComplete(docType.key, uploadId, newSessionId)
            }
            onUploadError={(error) => handleUploadError(docType.key, error)}
            disabled={isDisabled}
          />
        </Box>
      );
    },
    [dealId, formType, uploads, handleUploadStart, handleUploadComplete, handleUploadError]
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, pb: 4 }}>
      {/* Instructions */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Please upload the documents requested below. You can upload multiple files at once by
          selecting the appropriate document type for each file.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Tips:</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ mt: 1, pl: 2 }}>
          <li>Income and Expense statements must be in Excel format (.xlsx or .xls)</li>
          <li>STR Reports must be in Excel format (.xlsx or .xls)</li>
          <li>Other documents can be any file type (PDF, Word, Excel, images, etc.)</li>
          <li>Maximum file size: 100MB for Excel documents, 250MB for other files</li>
        </Typography>
      </Paper>

      {/* Document Sections */}
      {sections.map((section, index) => (
        <Box key={section.title} sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            {section.title}
          </Typography>
          {section.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {section.description}
            </Typography>
          )}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            {section.types.map((docType) => renderDropzone(docType))}
          </Box>
          {index < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
        </Box>
      ))}

      {/* Contact Information Section */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Divider sx={{ mb: 4 }} />
        <ContactInfoForm
          value={contactInfo}
          onChange={setContactInfo}
          errors={contactErrors}
        />
      </Box>

      {/* Submit/Session Error */}
      {(submitError || sessionError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError || sessionError}
        </Alert>
      )}

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            minWidth: 200,
            py: 1.5,
            fontSize: '1.1rem',
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
              Submitting...
            </>
          ) : (
            `Submit (${uploadedCount} document${uploadedCount !== 1 ? 's' : ''})`
          )}
        </Button>
      </Box>

      {/* Help Text */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', mt: 2 }}
      >
        By submitting, you confirm that you are authorized to upload these documents on behalf of{' '}
        {dealInfo.propertyName}.
      </Typography>
    </Box>
  );
};

export default DocumentUploadForm;
